/**
 * Tracked changes authored by "mytender.io" rather than the signed-in user.
 *
 * Office.js has no way to set a revision's author: anything written while
 * change tracking is on is stamped with the Word user. But Word keeps the
 * revision marks of OOXML it is handed, author and date included. So an edit
 * is written as a paragraph that already contains `<w:del>` (the old text)
 * and `<w:ins>` (the new text) marked with our author, inserted with
 * tracking switched off, and Word shows it as one deletion + one insertion
 * by mytender.io that the user accepts or rejects together.
 *
 * Used by `applyDocumentEdits` when the host allows tracking to be switched
 * off around the write (WordApi 1.4). A refused revision is reported as not
 * applied, never rewritten under the user's own name (see wordTools.ts).
 */

export const REVISION_AUTHOR = "mytender.io";

const W_NS = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";

/** Characters XML 1.0 forbids; Word's text can hold them (a cell mark, a field separator). */
// eslint-disable-next-line no-control-regex -- the point is to name them
const XML_FORBIDDEN = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]/g;

const escapeXml = (text: string): string =>
  text
    .replace(XML_FORBIDDEN, "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

let revisionCounter = 1;
const nextId = () => String(revisionCounter++);

const runXml = (text: string, deleted: boolean): string => {
  // Line breaks inside a paragraph become soft breaks; paragraph breaks are
  // the caller's business (one paragraph per call). Word reports a manual
  // line break (Shift+Enter) as U+000B, which XML forbids: it is the same
  // soft break.
  // eslint-disable-next-line no-control-regex
  const parts = text.replace(/\u000B/g, "\n").split("\n");
  return parts
    .map((part, i) => {
      const tag = deleted ? "w:delText" : "w:t";
      const br = i < parts.length - 1 ? "<w:br/>" : "";
      return `<w:r><${tag} xml:space="preserve">${escapeXml(part)}</${tag}>${br}</w:r>`;
    })
    .join("");
};

/**
 * A paragraph whose whole text is a tracked replacement.
 * @param oldText - What is there now (rendered as a deletion); "" for pure insertions.
 * @param newText - What should be there (rendered as an insertion).
 * @param styleId - The paragraph style id to keep (e.g. "Heading1"), if known.
 * @param author - The revision author.
 * @returns The `<w:p>` markup.
 */
export const revisionParagraphXml = (
  oldText: string,
  newText: string,
  styleId: string | null,
  author: string = REVISION_AUTHOR,
  options: { deleteParagraphMark?: boolean } = {}
): string => {
  const date = new Date().toISOString().replace(/\.\d{3}Z$/, "Z");
  const pStyle = styleId ? `<w:pStyle w:val="${escapeXml(styleId)}"/>` : "";
  // A deleted paragraph mark makes the whole paragraph go when accepted,
  // instead of leaving an empty line behind.
  const markDel = options.deleteParagraphMark
    ? `<w:rPr><w:del w:id="${nextId()}" w:author="${escapeXml(author)}" w:date="${date}"/></w:rPr>`
    : "";
  const pPr = pStyle || markDel ? `<w:pPr>${pStyle}${markDel}</w:pPr>` : "";
  const del = oldText
    ? `<w:del w:id="${nextId()}" w:author="${escapeXml(author)}" w:date="${date}">${runXml(oldText, true)}</w:del>`
    : "";
  const ins = newText
    ? `<w:ins w:id="${nextId()}" w:author="${escapeXml(author)}" w:date="${date}">${runXml(newText, false)}</w:ins>`
    : "";
  return `<w:p>${pPr}${del}${ins}</w:p>`;
};

/**
 * Wrap body paragraphs in the flat OPC package `insertOoxml` expects.
 * @param paragraphsXml - One or more `<w:p>` elements.
 * @returns The package markup.
 */
export const ooxmlPackage = (paragraphsXml: string): string =>
  `<pkg:package xmlns:pkg="http://schemas.microsoft.com/office/2006/xmlPackage">` +
  `<pkg:part pkg:name="/_rels/.rels" pkg:contentType="application/vnd.openxmlformats-package.relationships+xml">` +
  `<pkg:xmlData><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
  `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>` +
  `</Relationships></pkg:xmlData></pkg:part>` +
  `<pkg:part pkg:name="/word/document.xml" pkg:contentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml">` +
  `<pkg:xmlData><w:document xmlns:w="${W_NS}"><w:body>${paragraphsXml}</w:body></w:document></pkg:xmlData></pkg:part>` +
  `</pkg:package>`;

/** Word style names ("Heading 1") to style ids ("Heading1") for the common built-ins. */
export const styleIdFor = (styleName: string | null | undefined): string | null => {
  if (!styleName) return null;
  const trimmed = styleName.trim();
  if (/^normal$/i.test(trimmed)) return null;
  const heading = /^heading\s*([1-9])$/i.exec(trimmed);
  if (heading) return `Heading${heading[1]}`;
  if (/^title$/i.test(trimmed)) return "Title";
  if (/^subtitle$/i.test(trimmed)) return "Subtitle";
  if (/^list paragraph$/i.test(trimmed)) return "ListParagraph";
  return trimmed.replace(/\s+/g, "");
};

/**
 * Replace a paragraph's text with a mytender.io-authored tracked change.
 * @param paragraph - The paragraph to replace (loaded with text and style).
 * @param newText - The replacement text.
 * @param context - The Word request context.
 * @returns Nothing; the caller syncs.
 */
export const replaceParagraphAsAuthoredRevision = (
  paragraph: Word.Paragraph,
  oldText: string,
  newText: string,
  _styleName: string | null,
  author: string = REVISION_AUTHOR
): void => {
  // Only the paragraph's content is replaced, never its mark: the mark of
  // the last paragraph in a content control (a question's answer block)
  // belongs to the control, and Word refuses to replace that paragraph
  // whole. The paragraph keeps its style, so none is written here.
  const xml = ooxmlPackage(revisionParagraphXml(oldText, newText, null, author));
  paragraph.getRange("Content").insertOoxml(xml, Word.InsertLocation.replace);
};

/**
 * Insert a new paragraph after `paragraph` as a mytender.io-authored insertion.
 * @param paragraph - The anchor.
 * @param text - The new paragraph's text.
 * @param styleName - The style to give it (null for Normal).
 * @param author - The revision author.
 */
export const insertParagraphAfterAsAuthoredRevision = (
  paragraph: Word.Paragraph,
  text: string,
  styleName: string | null,
  author: string = REVISION_AUTHOR
): void => {
  const xml = ooxmlPackage(revisionParagraphXml("", text, styleIdFor(styleName), author));
  // A paragraph only takes OOXML at start/end/replace; its range takes it after.
  paragraph.getRange(Word.RangeLocation.whole).insertOoxml(xml, Word.InsertLocation.after);
};

/**
 * Delete a paragraph as a mytender.io-authored deletion (text and mark).
 * @param paragraph - The paragraph to delete.
 * @param oldText - Its current text.
 * @param styleName - Its style, kept on the deleted paragraph.
 * @param author - The revision author.
 */
/**
 * Mark a paragraph's text deleted but keep its mark: for the last paragraph
 * of a content control, whose mark Word will not let go of.
 */
export const deleteParagraphContentAsAuthoredRevision = (
  paragraph: Word.Paragraph,
  oldText: string,
  author: string = REVISION_AUTHOR
): void => {
  const xml = ooxmlPackage(revisionParagraphXml(oldText, "", null, author));
  paragraph.getRange("Content").insertOoxml(xml, Word.InsertLocation.replace);
};

/** The whole-paragraph form of {@link replaceParagraphAsAuthoredRevision}: mark and style included. */
export const replaceWholeParagraphAsAuthoredRevision = (
  paragraph: Word.Paragraph,
  oldText: string,
  newText: string,
  styleName: string | null,
  author: string = REVISION_AUTHOR
): void => {
  const xml = ooxmlPackage(revisionParagraphXml(oldText, newText, styleIdFor(styleName), author));
  paragraph.insertOoxml(xml, Word.InsertLocation.replace);
};

export const deleteParagraphAsAuthoredRevision = (
  paragraph: Word.Paragraph,
  oldText: string,
  styleName: string | null,
  author: string = REVISION_AUTHOR
): void => {
  const xml = ooxmlPackage(
    revisionParagraphXml(oldText, "", styleIdFor(styleName), author, { deleteParagraphMark: true })
  );
  paragraph.insertOoxml(xml, Word.InsertLocation.replace);
};
